import type {
  GenerateNativeMessageSenderOptions,
  NativeMessage,
  SendMessageToNative,
  SendMessageToNativeOptions
} from './contracts';

declare global {
  interface Window {
    nativeWebViewMessageHandler?: {
      postMessage: (event: string) => void;
    };
    webkit?: {
      messageHandlers: {
        nativeWebViewMessageHandler: {
          postMessage: (event: string) => void;
        };
      };
    };
  }
}

function toError(err: unknown) {
  return err instanceof Error ? err : new Error(String(err));
}

/**
 * Generates a `sendMessageToNative` function
 *
 * @example
 * import { type DispatcherEvent, generateNativeMessageSender } from '@carvana/vdp-native-hooks';
 *
 * export default generateNativeMessageSender<DispatcherEvent>({
 *   logError(error: Error) {
 *     console.error(error.message);
 *   },
 *   source: 'vdp',
 *   verboseLogs: isNonProd
 * });
 */

export default function generateNativeMessageSender<Event>({
  logError,
  source,
  verboseLogs
}: GenerateNativeMessageSenderOptions): SendMessageToNative<Event> {
  return function sendMessageToNative(
    event: Event,
    payload: Record<string, any>,
    { callback, onError }: SendMessageToNativeOptions = {}
  ): void {
    const message: NativeMessage<Event, Record<string, any>> = {
      event,
      payload,
      source
    };

    if (verboseLogs) {
      console.debug('Send message to native', message);
    }

    try {
      // Android
      if (
        typeof window.nativeWebViewMessageHandler?.postMessage === 'function'
      ) {
        window.nativeWebViewMessageHandler.postMessage(JSON.stringify(message));

        if (callback) callback();
      }

      // iOS
      if (
        typeof window.webkit?.messageHandlers?.nativeWebViewMessageHandler
          ?.postMessage === 'function'
      ) {
        window.webkit.messageHandlers.nativeWebViewMessageHandler.postMessage(
          JSON.stringify(message)
        );

        if (callback) callback();
      }
    } catch (err) {
      const error = toError(err);

      logError(error, { location: 'Native message dispatch' });

      if (onError) onError(error);
    }
  };
}
